import {ChangeDetectionStrategy, Component, inject, OnInit, signal} from '@angular/core';
import {TableTemplateComponentHost} from '@components/ui/table-template/table-template-component-host';
import {TableTemplateColumnConfig} from '@components/ui/table-template/models/table-template-column-config';
import {PageViewModel} from '@components/ui/table-template/models/page-view-model';
import {SessionStorageService} from '@shared/services/session-storage.service';
import {ClientsideDataService} from '@shared/services/clientside-data.service';
import {SortedTable} from '@shared/enums/sorted-table.enum';
import {DataViewModel} from '@components/ui/table-template/models/data-view-model';
import {CellType} from '@components/ui/table-template/enums/cell-type.enum';
import {Alignment} from '@components/ui/table-template/enums/alignment.enum';
import {EntityAccountConverterService} from './services/entity-account-converter.service';
import {EntityAccountService} from './services/entity-account.service';
import {EntityAccountViewModel} from './view-models/entity-account.view-model';
import {ModalService} from '@shared/services/modal.service';
import {EntityAccountDetailsModalComponent} from './entity-account-details-modal/entity-account-details-modal.component';
import {AppParameterMap} from '../../config/app-parameter-map';
import {PARAM_ENTITY_ID} from '../../config/app-urls';
import {Router} from '@angular/router';
import {Keys} from '@shared/keys';
import {LoadingAnimationComponent} from '@shared/components/loading-animation/loading-animation.component';
import {TableTemplateComponent} from '@components/ui/table-template/table-template.component';
import {EntityAccountCardContainerComponent} from './entity-account-card-container/entity-account-card-container.component';

@Component({
    selector: 'app-client-banking',
    templateUrl: './client-banking.component.html',
    styleUrls: ['./client-banking.component.scss'],
    providers: [ClientsideDataService],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [LoadingAnimationComponent, TableTemplateComponent, EntityAccountCardContainerComponent]
})
export class ClientBankingComponent implements OnInit, TableTemplateComponentHost {

  private readonly router = inject(Router);
  private readonly sessionStorageService = inject(SessionStorageService);
  private readonly entityAccountService = inject(EntityAccountService);
  private readonly clientSideDataService = inject(ClientsideDataService<EntityAccountViewModel>);
  private readonly entityAccountConverterService = inject(EntityAccountConverterService);
  private readonly modalService = inject(ModalService);

  readonly dataLoading = signal(true);
  readonly dataAvailable = signal(false);
  readonly dataEmpty = signal(false);

  readonly columns = signal<TableTemplateColumnConfig[]>([]);
  readonly sortField = signal('name');
  readonly sortOrder = signal(1);
  readonly searchString = signal('');
  readonly pageNumber = signal(1);
  readonly pageSize = signal(20);
  readonly currentPage = signal<PageViewModel<EntityAccountViewModel>>({
    totalElements: 0,
    pageNumber: 1,
    pageSize: 20,
    data: []
  });
  private readonly SEARCH_FIELDS: Keys<EntityAccountViewModel> = ['portfolioNumber', 'name', 'number'];

  ngOnInit(): void {
    this.dataLoading.set(true);
    this.dataEmpty.set(false);
    this.dataAvailable.set(false);

    this.populateColumns();
    const storedSort = this.sessionStorageService.getSort(SortedTable.Accounts);
    if (typeof storedSort[0] === 'string' && typeof storedSort[1] === 'number') {
      this.sortField.set(storedSort[0]);
      this.sortOrder.set(storedSort[1]);
    }

    const params = AppParameterMap.from(this.router);
    const entityId = params.get(PARAM_ENTITY_ID);

    this.entityAccountService.getEntityAccounts(entityId).subscribe(accounts => {
      const entityAccountVMs = this.entityAccountConverterService.convert(entityId, accounts);
      this.clientSideDataService.initialize(entityAccountVMs);
      this.updateData();
      this.dataLoading.set(false);
      this.dataEmpty.set(entityAccountVMs.length === 0);
      this.dataAvailable.set(!this.dataEmpty());
    });
  }

  sort(column: TableTemplateColumnConfig): void {
    this.updateSortColumn(column.dataProperty);
    this.pageNumber.set(1);
    this.updateData();
  }

  search(searchTerm: string): void {
    this.searchString.set(searchTerm);
    this.pageNumber.set(1);
    this.updateData();
  }

  page(targetPage: number): void {
    this.pageNumber.set(targetPage);
    this.updateData();
  }

  rowClicked(dataViewModel: DataViewModel): void {
    const entityAccountViewModel = dataViewModel as EntityAccountViewModel;
    this.modalService.openModal(EntityAccountDetailsModalComponent, entityAccountViewModel);
  }

  populateColumns() {
    this.columns.set([
      {
        title: 'Portfolio Number',
        dataProperty: 'portfolioNumber',
        cellType: CellType.Text,
        sortable: true,
        alignment: Alignment.Left,
        width: '15%',
        dataFocusable: true,
        headingFocusable: true,
        emptyFallback: '(Empty)'
      },
      {
        title: 'Name',
        dataProperty: 'name',
        cellType: CellType.Text,
        sortable: true,
        alignment: Alignment.Left,
        width: '35%',
        dataFocusable: true,
        headingFocusable: true,
        emptyFallback: '(Empty)'
      },
      {
        title: 'Account Number',
        dataProperty: 'number',
        cellType: CellType.Text,
        sortable: true,
        alignment: Alignment.Left,
        width: '20%',
        dataFocusable: false,
        headingFocusable: true,
        emptyFallback: '(Empty)'
      },
      {
        title: 'Currency',
        dataProperty: 'currency',
        cellType: CellType.Text,
        sortable: true,
        alignment: Alignment.Right,
        width: '15%',
        dataFocusable: false,
        headingFocusable: true,
        emptyFallback: '(Empty)'
      },
      {
        title: 'Balance',
        dataProperty: 'balance',
        cellType: CellType.Text,
        sortable: true,
        alignment: Alignment.Right,
        width: '15%',
        dataFocusable: false,
        headingFocusable: true
      }
    ]);
  }

  private updateSortColumn(field: string): void {
    if (this.sortField() === field) {
      this.sortOrder.set(this.sortOrder() * -1);
    } else {
      this.sortField.set(field);
      this.sortOrder.set(1);
    }

    this.sessionStorageService.setSort(SortedTable.Accounts, this.sortField(), this.sortOrder());
  }

  private updateData(): void {
    this.currentPage.set(this.clientSideDataService.generatePage(
      this.sortField(),
      this.sortOrder() === 1,
      this.SEARCH_FIELDS,
      this.searchString(),
      this.pageNumber(),
      this.pageSize()
    ));
  }
}
